import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, EllipsisVertical, StickyNote, AlertTriangle } from 'lucide-react';
import type { Engagement, Entity, JournalEntryHeader } from '../types';
import {
  getJournalEntryHeaders,
  getEntities,
  deleteJournalEntryHeader,
  updateSignoffState,
  acknowledgePrincipalReview,
} from '../db/database';
import MAdjCreateModal from './MAdjCreateModal';
import { displayAmountRounded } from '../utils/format';
import {
  isTierSigned,
  isTierClickable,
  computeNextSignoffState,
  getSignoffTitle,
  hasCautionFlag,
} from '../utils/signoff';
import type { SignoffTier } from '../utils/signoff';
import { Button } from './Button';
import { SignoffCheckbox } from './SignoffCheckbox';
import MAdjustmentEditor from './MAdjustmentEditor';

// ============================================================================
// RowKebabMenu
// ============================================================================

interface RowKebabMenuProps {
  onDelete: () => void;
}

function RowKebabMenu({ onDelete }: RowKebabMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOpenUpwards(window.innerHeight - rect.bottom < 150);
    }
    setIsOpen((v) => !v);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-1.5 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded transition-colors"
        title="More actions"
      >
        <EllipsisVertical className="w-5 h-5 text-slate-500 dark:text-slate-400" />
      </button>
      {isOpen && (
        <div
          className={`absolute right-0 w-36 glass-panel rounded-lg py-1 z-50 ${
            openUpwards ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); setIsOpen(false); }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2 text-red-600 dark:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAdjustmentList
// ============================================================================

interface MAdjustmentListProps {
  engagement: Engagement;
  initialHeader?: JournalEntryHeader | null;
  onInitialHeaderConsumed?: () => void;
}

export default function MAdjustmentList({ engagement, initialHeader, onInitialHeaderConsumed }: MAdjustmentListProps) {
  const [headers, setHeaders] = useState<JournalEntryHeader[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedHeader, setSelectedHeader] = useState<JournalEntryHeader | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<JournalEntryHeader | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── Load — filter to M-Adj headers only ───────────────────────────────────

  const loadHeaders = useCallback(async () => {
    if (!engagement.id) return;
    setIsLoading(true);
    try {
      const [all, entityList] = await Promise.all([
        getJournalEntryHeaders(engagement.id),
        getEntities(engagement.id),
      ]);
      setHeaders(all.filter((h) => h.netAdjustmentCents !== undefined));
      setEntities(entityList);
    } catch (err) {
      console.error('MAdjustmentList load failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [engagement.id]);

  useEffect(() => { loadHeaders(); }, [loadHeaders]);

  // Deep-link: if a specific header was passed in, open it immediately on mount
  useEffect(() => {
    if (initialHeader) setSelectedHeader(initialHeader);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Signoff handlers ──────────────────────────────────────────────────────

  const handleSign = useCallback(async (header: JournalEntryHeader, tier: SignoffTier) => {
    const signed = isTierSigned(tier, header.signoffState);
    const cautionActive = hasCautionFlag(header);

    if (signed && tier === 'Principal' && cautionActive) {
      const ts = Date.now();
      setHeaders((prev) => prev.map((h) => h.id === header.id ? { ...h, lastPrincipalReviewAt: ts } : h));
      try {
        await acknowledgePrincipalReview(header.id!);
      } catch (err) {
        console.error('Failed to re-sign:', err);
        setHeaders((prev) => prev.map((h) => h.id === header.id ? { ...h, lastPrincipalReviewAt: header.lastPrincipalReviewAt } : h));
      }
      return;
    }
    if (signed) return;

    const next = computeNextSignoffState(header.signoffState, tier);
    setHeaders((prev) => prev.map((h) => h.id === header.id ? { ...h, signoffState: next } : h));
    try {
      await updateSignoffState(header.id!, next);
    } catch (err) {
      console.error('Failed to update signoff:', err);
      setHeaders((prev) => prev.map((h) => h.id === header.id ? { ...h, signoffState: header.signoffState } : h));
    }
  }, []);

  const handleRemove = useCallback(async (header: JournalEntryHeader, tier: SignoffTier) => {
    if (!isTierClickable(tier, header.signoffState)) return;
    const next = computeNextSignoffState(header.signoffState, tier);
    setHeaders((prev) => prev.map((h) => h.id === header.id ? { ...h, signoffState: next } : h));
    try {
      await updateSignoffState(header.id!, next);
    } catch (err) {
      console.error('Failed to remove signoff:', err);
      setHeaders((prev) => prev.map((h) => h.id === header.id ? { ...h, signoffState: header.signoffState } : h));
    }
  }, []);

  // ── Delete handler ────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      await deleteJournalEntryHeader(deleteTarget.id);
      setDeleteTarget(null);
      await loadHeaders();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Editor view ───────────────────────────────────────────────────────────

  if (selectedHeader) {
    return (
      <MAdjustmentEditor
        header={selectedHeader}
        engagement={engagement}
        entities={entities}
        onBack={(reload) => {
          if (initialHeader && selectedHeader?.id === initialHeader.id) {
            onInitialHeaderConsumed?.();
          }
          setSelectedHeader(null);
          if (reload) loadHeaders();
        }}
      />
    );
  }

  // ── Shared th class ───────────────────────────────────────────────────────

  const thBase =
    'px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide select-none';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-screen bg-taxtrail-bg dark:bg-neutral-900 transition-colors duration-300">

      {/* ── CONTROL BAR ─────────────────────────────────────────────────── */}
      <div className="sticky top-0 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-700 z-20 transition-colors duration-300">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">#</span>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              M-Adjustments
            </h1>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            {engagement.clientName}
          </p>
          <div className="flex gap-3 items-center">
            <div className="flex-1" />
            <Button
              variant="primary"
              size="md"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              NEW M-ADJUSTMENT
            </Button>
          </div>
        </div>
      </div>

      {/* ── TABLE ────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden p-6">
        <div
          className="h-full overflow-auto border border-gray-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-transparent transition-colors duration-300"
          style={{ scrollbarGutter: 'stable' }}
        >
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-slate-100 dark:bg-neutral-800 border-b border-slate-200 dark:border-neutral-700 z-10">
              <tr>
                <th className={`${thBase} text-left w-32`}>Ref #</th>
                <th className={`${thBase} text-right w-40`}>Net Adjustment</th>
                <th className={`${thBase} text-left`}>Name</th>
                <th className="px-2 py-3 w-10" />
                <th className={`${thBase} text-center w-14`}>Prep</th>
                <th className={`${thBase} text-center w-14`}>Prin</th>
                <th className={`${thBase} text-center w-14`}>Final</th>
                <th className="px-4 py-3 w-12" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-20 text-slate-500 dark:text-slate-400 text-sm">
                    Loading…
                  </td>
                </tr>
              ) : headers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-20 text-slate-500 dark:text-slate-400">
                    <span className="text-4xl block mb-3 opacity-20">#</span>
                    <p className="text-sm font-medium">No M-Adjustments yet.</p>
                    <p className="text-xs mt-1 text-slate-400 dark:text-slate-600">
                      Click "New M-Adjustment" to get started.
                    </p>
                  </td>
                </tr>
              ) : (
                headers.map((header) => {
                  const netAdj = displayAmountRounded(header.netAdjustmentCents ?? 0);
                  const isNegAdj = (header.netAdjustmentCents ?? 0) < 0;
                  return (
                    <tr
                      key={header.id}
                      onDoubleClick={() => setSelectedHeader(header)}
                      className="border-b border-slate-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-slate-50 dark:hover:bg-neutral-800 cursor-pointer transition-colors"
                    >
                      {/* Ref # */}
                      <td className="px-4 py-3 text-sm font-mono font-medium text-slate-800 dark:text-slate-100 whitespace-nowrap">
                        {header.jeNumber}
                      </td>
                      {/* Net Adjustment */}
                      <td className={`px-4 py-3 text-sm font-mono text-right whitespace-nowrap ${isNegAdj ? 'text-taxtrail-red' : 'text-slate-800 dark:text-slate-100'}`}>
                        {netAdj.text}
                      </td>
                      {/* Name */}
                      <td className="px-4 py-3 text-sm text-slate-800 dark:text-slate-100">
                        <span className="flex items-center gap-1.5">
                          {header.jeName}
                          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                            header.differenceType === 'Temporary'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              : 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                          }`}>
                            {header.differenceType === 'Temporary' ? 'TEMP' : 'PERM'}
                          </span>
                          {header.notes && (
                            <span title={header.notes} className="inline-flex">
                              <StickyNote className="w-3.5 h-3.5 shrink-0 text-amber-500 dark:text-amber-400" />
                            </span>
                          )}
                        </span>
                      </td>
                      {/* Caution flag */}
                      <td className="px-2 py-3 text-center">
                        {hasCautionFlag(header) && (
                          <span title="Edited after Principal review">
                            <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-amber-400 inline" />
                          </span>
                        )}
                      </td>
                      {/* Prepared */}
                      <td className="px-2 py-3 text-center">
                        <SignoffCheckbox
                          tier="Prepared"
                          isSigned={isTierSigned('Prepared', header.signoffState)}
                          isClickable={isTierClickable('Prepared', header.signoffState)}
                          title={getSignoffTitle('Prepared', isTierSigned('Prepared', header.signoffState), isTierClickable('Prepared', header.signoffState), header.signoffState)}
                          onSign={() => handleSign(header, 'Prepared')}
                          onRemove={() => handleRemove(header, 'Prepared')}
                        />
                      </td>
                      {/* Principal */}
                      <td className="px-2 py-3 text-center">
                        <SignoffCheckbox
                          tier="Principal"
                          isSigned={isTierSigned('Principal', header.signoffState)}
                          isClickable={isTierClickable('Principal', header.signoffState)}
                          title={getSignoffTitle('Principal', isTierSigned('Principal', header.signoffState), isTierClickable('Principal', header.signoffState), header.signoffState, hasCautionFlag(header))}
                          hasCautionFlag={hasCautionFlag(header)}
                          onSign={() => handleSign(header, 'Principal')}
                          onRemove={() => handleRemove(header, 'Principal')}
                        />
                      </td>
                      {/* Final */}
                      <td className="px-2 py-3 text-center">
                        <SignoffCheckbox
                          tier="Final"
                          isSigned={isTierSigned('Final', header.signoffState)}
                          isClickable={isTierClickable('Final', header.signoffState)}
                          title={getSignoffTitle('Final', isTierSigned('Final', header.signoffState), isTierClickable('Final', header.signoffState), header.signoffState)}
                          onSign={() => handleSign(header, 'Final')}
                          onRemove={() => handleRemove(header, 'Final')}
                        />
                      </td>
                      {/* Kebab */}
                      <td className="px-2 py-3">
                        <RowKebabMenu onDelete={() => setDeleteTarget(header)} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── CREATE MODAL ─────────────────────────────────────────────────── */}
      {isCreateModalOpen && (
        <MAdjCreateModal
          engagement={engagement}
          entities={entities}
          nextNumber={headers.length + 1}
          onCreated={(newHeader) => {
            setIsCreateModalOpen(false);
            setSelectedHeader(newHeader);
            loadHeaders();
          }}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}

      {/* ── DELETE CONFIRMATION ───────────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4">
          <div className="glass-panel max-w-md w-full p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">
              Delete M-Adjustment?
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              Deleting{' '}
              <span className="font-bold text-slate-900 dark:text-slate-100">
                {deleteTarget.jeNumber} — {deleteTarget.jeName}
              </span>{' '}
              will also delete all of its line items. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isDeleting ? 'Deleting…' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
